import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/useLanguage';
import { cn } from '../../lib/utils';

type SheetBackPhonePlacement = 'floating' | 'inline' | 'hidden';

/** Consistent back control for guest sheets / assistants. */
export const SheetBackButton: React.FC<{
  onClick: () => void;
  className?: string;
  label?: string;
  /**
   * Phone (&lt;744px) placement:
   * - `floating` (default) — fixed bottom-left thumb zone (portaled to body)
   * - `inline` — render in place (e.g. sticky footer row)
   * - `hidden` — phone only suppressed (pad/desktop still show inline)
   */
  phone?: SheetBackPhonePlacement;
}> = ({ onClick, className, label, phone = 'floating' }) => {
  const { t } = useLanguage();
  const text = label ?? t('common.goBack') ?? 'Back';

  const handleClick = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
    onClick();
  };

  const labelNode = (
    <>
      <ChevronLeft className="h-6 w-6" strokeWidth={2.25} aria-hidden />
      <span className="pr-1">{text}</span>
    </>
  );

  const inlineClass =
    className ??
    'inline-flex shrink-0 items-center gap-0.5 rounded-xl px-2 py-2 text-sm font-semibold text-[#3d2817] transition-colors hover:bg-black/5 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-nature-500';

  if (phone === 'inline') {
    return (
      <button type="button" onClick={handleClick} className={cn(inlineClass, 'active:scale-[0.98]')} aria-label={text}>
        {labelNode}
      </button>
    );
  }

  const floating =
    phone === 'floating' && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="pointer-events-none fixed bottom-0 left-0 z-[10070] min-[744px]:hidden"
            style={{
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              // Sit in the dock clearance gap (panel bottom inset ~5.25rem).
              paddingBottom:
                'max(0.75rem, calc(env(safe-area-inset-bottom, 0px) + 0.5rem))',
            }}
          >
            <button
              type="button"
              onClick={handleClick}
              className="pointer-events-auto inline-flex items-center gap-0.5 rounded-2xl border border-[#3d2817]/12 bg-white/95 px-3 py-2.5 text-sm font-semibold text-[#3d2817] shadow-[0_8px_24px_rgba(61,40,23,0.16)] backdrop-blur-md transition-transform hover:bg-white active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-nature-500"
              aria-label={text}
            >
              {labelNode}
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          inlineClass,
          phone === 'floating' || phone === 'hidden' ? 'max-[743px]:hidden' : null,
        )}
        aria-label={text}
      >
        {labelNode}
      </button>
      {floating}
    </>
  );
};

export default SheetBackButton;
